import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { priceGroupLabel } from '@erp/shared';
import { SearchCombobox } from '@/components/common/search-combobox';
import { Button } from '@/components/ui/button';
import { usePriceGroups } from '../api/use-price-groups';

export function PriceGroupSelect({
  id,
  value,
  onChange,
  disabled = false,
  currency,
  legacyLabel,
}: {
  id?: string;
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  currency?: string;
  legacyLabel?: string | null | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const groups = usePriceGroups();
  const [query, setQuery] = useState('');
  const label = (entry: NonNullable<typeof groups.data>['data'][number]) =>
    `${entry.code} · ${priceGroupLabel(entry, i18n.language)}${entry.isActive ? '' : ` (${t('priceGroups.inactive')})`}`;
  const selected = groups.data?.data.find((entry) => entry.id === value);
  const options = (groups.data?.data ?? [])
    .filter(
      (entry) =>
        (entry.id === value ||
          (entry.isActive && (!currency || !entry.currency || entry.currency === currency))) &&
        `${entry.code} ${entry.name} ${Object.values(entry.translations).join(' ')}`
          .toLocaleLowerCase()
          .includes(query.toLocaleLowerCase()),
    )
    .map((entry) => ({ id: entry.id, label: label(entry) }));
  return (
    <SearchCombobox
      id={id}
      value={selected ? label(selected) : legacyLabel || t('priceGroups.none')}
      placeholder={t('priceGroups.search')}
      options={[{ id: 'NONE', label: t('priceGroups.none') }, ...options]}
      isLoading={groups.isPending}
      isError={groups.isError}
      onRetry={() => {
        void groups.refetch();
      }}
      disabled={disabled}
      emptyLabel={t('priceGroups.empty')}
      ariaLabel={t('priceGroups.field')}
      onQueryChange={setQuery}
      onSelect={(option) => {
        onChange(option.id === 'NONE' ? null : option.id);
        setQuery('');
      }}
    />
  );
}

export function PriceGroupFilter({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const groups = usePriceGroups();
  const [query, setQuery] = useState('');
  const options = [
    { id: 'NONE', label: t('priceGroups.none') },
    ...(groups.data?.data ?? []).map((entry) => ({
      id: entry.id,
      label: `${entry.code} · ${priceGroupLabel(entry, i18n.language)}${entry.isActive ? '' : ` (${t('priceGroups.inactive')})`}`,
    })),
  ];
  return (
    <div className="min-w-60 space-y-1">
      <SearchCombobox
        value=""
        placeholder={t('priceGroups.field')}
        ariaLabel={t('priceGroups.filter')}
        multiple
        selectedIds={value}
        options={options.filter((entry) =>
          entry.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
        )}
        emptyLabel={t('priceGroups.empty')}
        isLoading={groups.isPending}
        isError={groups.isError}
        onRetry={() => {
          void groups.refetch();
        }}
        onQueryChange={setQuery}
        onSelect={(option) =>
          onChange(
            value.includes(option.id)
              ? value.filter((id) => id !== option.id)
              : [...value, option.id],
          )
        }
      />
      <div className="flex flex-wrap gap-1">
        {value.map((id) => (
          <Button
            key={id}
            variant="secondary"
            size="sm"
            aria-label={`${t('common.remove')} ${options.find((entry) => entry.id === id)?.label ?? id}`}
            onClick={() => onChange(value.filter((entry) => entry !== id))}
          >
            {options.find((entry) => entry.id === id)?.label ?? id} ×
          </Button>
        ))}
      </div>
    </div>
  );
}
